// Track 5.1 — Yaw sweep prompt overlay.
//
// Shows after baseline lock with a "Run yaw sweep" CTA. Once running, it
// renders the current phase's instruction big and centered with a progress
// pip per phase. The sweep is short (≤12s), so we don't bother with
// dismiss/snooze logic — the rider either runs it now or skips for the session.

import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { YawSweepPhase } from '../vision/yawSweep';
import { colors } from '../theme';

interface Props {
  phase: YawSweepPhase;
  prompt: string | null;
  onStart: () => void;
  onSkip: () => void;
  /** CdA at 10° yaw — present only after a successful fit. */
  cdaAt10Deg?: number | null;
  baselineCda?: number | null;
}

const ACTIVE_PHASES: YawSweepPhase[] = [
  'look_left',
  'return_left',
  'look_right',
  'return_right',
  'fitting',
];

export default function YawSweepPrompt({
  phase,
  prompt,
  onStart,
  onSkip,
  cdaAt10Deg,
  baselineCda,
}: Props) {
  const { t } = useTranslation('session');

  if (phase === 'idle') return null;

  if (phase === 'waiting') {
    return (
      <Card>
        <Typography sx={titleSx}>{t('yaw.title')}</Typography>
        <Typography sx={bodySx}>{t('yaw.intro')}</Typography>
        <Box sx={{ display: 'flex', gap: 1.5, mt: 2 }}>
          <Button
            variant="contained"
            size="small"
            onClick={onStart}
            sx={{
              backgroundColor: colors.brand,
              color: colors.textInverse,
              fontWeight: 700,
              textTransform: 'none',
            }}
          >
            {t('yaw.start')}
          </Button>
          <Button
            variant="text"
            size="small"
            onClick={onSkip}
            sx={{ color: colors.textMuted, textTransform: 'none' }}
          >
            {t('yaw.skip')}
          </Button>
        </Box>
      </Card>
    );
  }

  if (ACTIVE_PHASES.includes(phase)) {
    return (
      <Card>
        <Typography sx={titleSx}>{t('yaw.sweeping')}</Typography>
        <Typography
          sx={{
            fontSize: '1.25rem',
            fontWeight: 700,
            color: colors.brand,
            mt: 1,
            mb: 0.5,
          }}
        >
          {prompt}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, mt: 1.5 }}>
          {['look_left', 'return_left', 'look_right', 'return_right', 'fitting'].map((p) => (
            <Box
              key={p}
              sx={{
                flex: 1,
                height: 4,
                borderRadius: 2,
                backgroundColor:
                  phase === p
                    ? colors.brand
                    : ACTIVE_PHASES.indexOf(phase) > ACTIVE_PHASES.indexOf(p as YawSweepPhase)
                      ? colors.brand
                      : colors.bgSubtle,
              }}
            />
          ))}
        </Box>
      </Card>
    );
  }

  if (phase === 'done' && cdaAt10Deg != null && baselineCda != null) {
    const deltaPct = ((cdaAt10Deg - baselineCda) / baselineCda) * 100;
    return (
      <Card>
        <Typography sx={titleSx}>{t('yaw.done')}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 1 }}>
          <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: colors.text }}>
            {cdaAt10Deg.toFixed(3)}
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: colors.textMuted }}>
            m² @ 10° yaw
          </Typography>
        </Box>
        <Typography sx={{ fontSize: '0.8125rem', color: colors.textMuted, mt: 0.75 }}>
          {t('yaw.deltaVsBaseline', {
            sign: deltaPct >= 0 ? '+' : '',
            pct: deltaPct.toFixed(1),
          })}
        </Typography>
      </Card>
    );
  }

  if (phase === 'failed') {
    return (
      <Card>
        <Typography sx={titleSx}>{t('yaw.failedTitle')}</Typography>
        <Typography sx={bodySx}>{t('yaw.failedBody')}</Typography>
        <Button
          variant="text"
          size="small"
          onClick={onStart}
          sx={{ color: colors.brand, textTransform: 'none', mt: 1 }}
        >
          {t('yaw.retry')}
        </Button>
      </Card>
    );
  }

  return null;
}

const titleSx = {
  fontSize: '0.6875rem',
  fontWeight: 600,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: colors.textMuted,
};

const bodySx = {
  fontSize: '0.875rem',
  color: colors.text,
  lineHeight: 1.5,
  mt: 0.75,
};

function Card({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: 3,
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`,
      }}
    >
      {children}
    </Box>
  );
}
